/**
 * Daily price of a vehicle for a stay (display and sort only: totals of record come from the quote).
 * Bikes have price steps (`pricingTiers`): the server prices a stay of n billable days at the step with the
 * highest `days` ≤ n, else at the base price (`back/controllers/reservationController.js` resolveBikeStayPricing,
 * `bestTier` in reservationPricing.ts). PR-07.
 */
import type { VehicleSummary } from '@/services/domain';

type Priced = Pick<VehicleSummary, 'pricePerDay' | 'tiers' | 'tierPricePerDay' | 'tierFromDays'>;

export function dailyPrice(v: Priced, days: number): number {
  if (v.tiers?.length) {
    let best: { fromDays: number; pricePerDay: number } | undefined;
    for (const t of v.tiers) if (t.fromDays <= days && (!best || t.fromDays > best.fromDays)) best = t;
    return best?.pricePerDay || v.pricePerDay;
  }
  // One step only (demo data): from `tierFromDays` (2 when not given).
  return v.tierPricePerDay && days >= (v.tierFromDays ?? 2) ? v.tierPricePerDay : v.pricePerDay;
}

/** The steps shown as «Dès N jours : X / jour»: from 2 days, cheaper than the base price, by ascending days. */
export function tierSteps(v: Priced): { fromDays: number; pricePerDay: number }[] {
  if (v.tiers?.length)
    return v.tiers.filter((t) => t.fromDays > 1 && t.pricePerDay > 0 && t.pricePerDay < v.pricePerDay);
  return v.tierPricePerDay !== undefined ? [{ fromDays: v.tierFromDays ?? 2, pricePerDay: v.tierPricePerDay }] : [];
}
